import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronDown, ExternalLink, Trash2 } from 'lucide-react';
import type { QuotationListItemDto, QuotationStatus } from '../../lib/quotationApi';
import { updateStatus, deleteQuotation, sendQuotationErrorMessage } from '../../lib/quotationApi';
import { formatDate as fmtDate } from "../../utils/dateFormat";
import { clientDisplayName } from '../../utils/orderIdentity';
import { quotationVesselName } from '../../utils/quotationVessel';
import Button from '../../components/ui/Button';
import Pagination from '../../components/ui/Pagination';
import StatusBadge from '../../components/StatusBadge';
import { EmptyState } from '../../components/ui/States';
import { useConfirm } from '../../components/ui/ConfirmProvider';
import ErrorModal from '../../components/ErrorModal';
import { formatCurrency } from '../../utils/priceUtils';

/** The statuses a quote can be moved to from this list, in the order the menu offers them. */
const STATUS_CHOICES: { value: QuotationStatus; label: string }[] = [
  { value: 'needs_review', label: 'Needs review' },
  { value: 'finalized', label: 'Finalized' },
  { value: 'sent', label: 'Sent' },
  { value: 'rejected', label: 'Rejected' },
];

/** Room the status menu needs below its chip (4 statuses, a divider and Delete), in px. */
const STATUS_MENU_HEIGHT = 250;

interface Props {
  quotations: QuotationListItemDto[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  onPageChange: (page: number) => void;
}


export const QuotationTable: React.FC<Props> = ({ quotations, pagination, onPageChange }) => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  // Where the open menu is drawn. It is drawn on the page, not in the cell: the table's own
  // `overflow-x-auto` / `overflow-hidden` wrappers cut off an absolutely positioned child, so on a
  // short list Rejected and Delete could not be reached (B006). Same pattern as ExportDropdown.
  const [menuPos, setMenuPos] = useState<{ top?: number; bottom?: number; left: number } | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  // A refused or failed change is told in the app's own dialog (ErrorModal), never window.alert.
  const [refusal, setRefusal] = useState<{ title: string; message: string } | null>(null);

  const formatDate = (dateString: string) => {
    return fmtDate(dateString);
  };

  // Colour comes from StatusBadge's registered map (`status-string-screen`) — this only names
  // the status. It used to carry its own four-colour map, one of about 25 in the app.
  const formatStatus = (status: string) =>
    STATUS_CHOICES.find((c) => c.value === status)?.label ?? status;

  const handleRowClick = (quotationId: string) => {
    navigate(`/quotations/${quotationId}`);
  };

  const handleStatusChange = async (quotationId: string, newStatus: QuotationStatus) => {
    try {
      console.log('[STATUS UPDATE] Updating quotation:', quotationId, 'to status:', newStatus);
      setUpdatingStatus(quotationId);
      const response = await updateStatus(quotationId, newStatus);
      console.log('[STATUS UPDATE] Response:', response);
      setOpenDropdown(null);

      // Moving to Finalized / Sent / Rejected also writes this quotation's
      // unpriced-but-costed lines into the Master Catalogue
      // (`quotationCatalogueHarvest.service.js`). A SUCCESS is deliberately
      // silent here — the reload below would wipe any toast a moment later, and
      // the durable receipt is the "N items added" panel on the quotation's own
      // detail page, which is where somebody can also remove one.
      //
      // A FAILURE is not silent. Those rows did not reach the catalogue and
      // nothing else on this screen would ever say so; `alert` because it
      // survives the reload, and because it is what this file already uses to
      // report a failed status change.
      const failures = response.catalogueHarvest?.failures || [];
      if (failures.length > 0) {
        alert(
          `Status updated, but ${failures.length} item${failures.length === 1 ? '' : 's'} could not be ` +
            `added to the Master Catalogue:\n\n` +
            failures.map((f) => `• ${f.itemName || `line ${f.lineIndex + 1}`} — ${f.message}`).join('\n') +
            `\n\nThe quotation itself is unaffected.`
        );
      }

      // Refresh the page to show updated status
      window.location.reload();
    } catch (error) {
      // A quotation with lines that still have no cost cannot be called
      // Finalized or Sent (TECH_DEBT #260). That is a refusal, not a fault, and
      // the server's message names the offending lines — so it is shown as-is
      // and nothing is logged as an error. Switched on the CODE, never on the
      // prose: a copy edit to the message must not change what happens here.
      const err = error as Error & { code?: string };
      // D51: a line whose amount is not the vessel's ask in its own unit is the
      // same kind of refusal, and its message names the lines and the figure.
      if (err.code === 'QUOTATION_HAS_UNPRICED_LINES' || err.code === 'QUOTATION_HAS_AMOUNTS_NOT_THE_ASK') {
        setRefusal({ title: 'Cannot change the status', message: err.message });
        return;
      }
      // D105.2: a line at US$0.00 cannot be marked Sent - the same door as Send quote. Worded from
      // the CODE with the lines listed (the one spelling the send form uses), never the prose.
      if (err.code === 'QUOTATION_NOT_SENDABLE') {
        setRefusal({ title: 'Cannot change the status', message: sendQuotationErrorMessage(err) });
        return;
      }
      console.error('[STATUS UPDATE] Failed to update status:', error);
      setRefusal({ title: 'Status not changed', message: 'Failed to update the status. Please try again.' });
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleDelete = async (quotationId: string) => {
    const ok = await confirm({
      title: 'Delete this quotation?',
      summary: 'This cannot be undone.',
      tone: 'error',
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    try {
      setUpdatingStatus(quotationId);
      await deleteQuotation(quotationId);
      setOpenDropdown(null);
      window.location.reload();
    } catch (error) {
      console.error('[DELETE] Failed to delete quotation:', error);
      setRefusal({ title: 'Delete failed', message: 'Failed to delete the quotation. Please try again.' });
    } finally {
      setUpdatingStatus(null);
    }
  };

  const toggleDropdown = (e: React.MouseEvent, quotationId: string) => {
    e.stopPropagation();
    if (openDropdown === quotationId) {
      setOpenDropdown(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    // Opens upward when there is no room for the menu (about 6 rows) below the chip.
    const opensUp = rect.bottom + STATUS_MENU_HEIGHT > window.innerHeight && rect.top > STATUS_MENU_HEIGHT;
    setMenuPos(
      opensUp
        ? { bottom: window.innerHeight - rect.top + 4, left: rect.left }
        : { top: rect.bottom + 4, left: rect.left }
    );
    setOpenDropdown(quotationId);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (openDropdown) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    // A fixed menu does not follow its row, so it closes when the page moves under it.
    window.addEventListener('scroll', handleClickOutside, true);
    window.addEventListener('resize', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('scroll', handleClickOutside, true);
      window.removeEventListener('resize', handleClickOutside);
    };
  }, [openDropdown]);

  const getRfqMongoId = (q: QuotationListItemDto): string | null => {
    if (typeof q.rfqId === 'object' && q.rfqId && '_id' in q.rfqId) {
      return String((q.rfqId as { _id: string })._id);
    }
    if (typeof q.rfqId === 'string' && /^[a-f\d]{24}$/i.test(q.rfqId)) {
      return q.rfqId;
    }
    return null;
  };

  /**
   * The populated RFQ behind a quotation, when the list endpoint sent one.
   *
   * `rfqId` arrives in two live shapes — a raw id string, or the populated
   * document — so both the client name and the vessel have to normalise before
   * they can fall back to it.
   */
  const rfqOf = (q: QuotationListItemDto) =>
    typeof q.rfqId === 'object' && q.rfqId ? q.rfqId : null;

  if (quotations.length === 0) {
    return (
      <div className="p-4">
        <EmptyState
          title="No quotations found"
          detail="Try adjusting your filters, or generate a quote from an RFQ in the inbox."
        />
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      {/* One table style, shared with every other module: `.data-table` in
          index.css draws the header band, the cell padding and the row hover
          (docs/DESIGN.md). This grid used to hand-draw all three, in a header
          grey and a border colour nothing else in the app used. */}
      <div className="overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              {/* D104.24: the QUO number — what a quotation is called everywhere else. */}
              <th className="w-[104px]">Quotation</th>
              {/* Email subject (from RFQ) */}
              <th className="min-w-[160px] max-w-[min(420px,40vw)]">Email subject</th>
              {/* Client Name Header */}
              <th>Client name</th>
              {/* Vessel Name Header — replaced "Email By" (Deborah, 2026-09-04:
                  "you definitely need the vessel name", and on the sender
                  column, "I don't think email is the thing you want to see
                  every time"). An order is identified by its ship: one client
                  sends RFQs for many vessels, and the sender's address
                  distinguishes none of them.

                  CATEGORY COLUMN INSERTION POINT. Deborah also asked for
                  "something like category, like what is food, whether is cabin,
                  whether is safety". It is deliberately NOT built here: the
                  category vocabulary is being settled separately
                  (`ItemCategory`), and a column rendering a field that is not
                  agreed yet is the same mistake as the blank Vessel column this
                  one replaces. Add it immediately after this <th> (and the
                  matching cell below). */}
              <th>Vessel name</th>
              {/* Created Header */}
              <th className="w-[112px]">Created</th>
              {/* Total Quote Header */}
              <th className="w-[120px] text-right">Total quote</th>
              {/* Total Items Header */}
              <th className="w-[80px] text-right">Items</th>
              {/* Status Header */}
              <th className="w-[120px]">Status</th>
              {/* Action Header */}
              <th className="w-[56px]">
                <span className="sr-only">Open</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {quotations.map((quotation) => {
              const rfq = rfqOf(quotation);
              // The quotation's own snapshot first, then the RFQ behind it.
              // Client Name read 'N/A' on 6 of 14 QA rows purely because the
              // snapshot was blank while the populated RFQ had the answer all
              // along — the list endpoint already selects `customerName` and
              // `vesselName`, so this costs no extra request.
              const clientName =
                clientDisplayName(quotation.customer?.customerName) ||
                clientDisplayName(rfq?.customerName) ||
                'N/A';
              // D104.12: the vessel is the mirrored owner's rule, the one the quotation's header, send
              // form, Client view and documents read; only the blank is this list's own.
              const vesselName = quotationVesselName(quotation) || 'N/A';
              const rawSubject =
                quotation.rfqSubject ??
                (typeof quotation.rfqId === 'object' && quotation.rfqId?.subject
                  ? quotation.rfqId.subject
                  : null);
              const emailSubject =
                rawSubject != null && String(rawSubject).trim() !== '' ? String(rawSubject).trim() : 'N/A';
              const rfqMongoId = getRfqMongoId(quotation);
              const order = quotation.confirmedOrder;

              return (
                <tr
                  key={quotation._id}
                  className="hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => handleRowClick(quotation._id)}
                >
                  <td className="whitespace-nowrap font-medium text-slate-900">{quotation.quotationNumber || '--'}</td>
                  {/* Email subject (from RFQ) */}
                  <td className="min-w-[160px] max-w-[min(420px,40vw)]">
                    <div className="flex min-w-0 items-center gap-1.5">
                      <span
                        className="min-w-0 flex-1 truncate text-left"
                        title={emailSubject === 'N/A' ? undefined : emailSubject}
                      >
                        {emailSubject}
                      </span>
                      {rfqMongoId && (
                        <Link
                          to={`/rfq?selected=${encodeURIComponent(rfqMongoId)}`}
                          state={{ selectedRfqId: rfqMongoId }}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex shrink-0 items-center justify-center rounded p-0.5 text-primary-500 hover:bg-primary-50"
                          title="Open RFQ in inbox"
                          aria-label="Open RFQ in inbox"
                        >
                          <ExternalLink aria-hidden="true" className="h-4 w-4" />
                        </Link>
                      )}
                    </div>
                  </td>
                  {/* Client Name Cell */}
                  <td className="max-w-[200px] truncate" title={clientName === 'N/A' ? undefined : clientName}>{clientName}</td>
                  {/* Vessel Name Cell (category column goes after this one) */}
                  <td className="max-w-[160px] truncate" title={vesselName === 'N/A' ? undefined : vesselName}>
                    {vesselName}
                  </td>
                  {/* Created Cell */}
                  <td className="whitespace-nowrap">{formatDate(quotation.createdAt)}</td>
                  {/* Total Quote Cell */}
                  {/* `summary?.`: a row written straight to the database can lack it, and one such row
                      threw here and blanked the whole list (B022). Missing reads N/A, never 0. */}
                  <td className="text-right tabular-nums">
                    {quotation.summary?.totalValue > 0
                      ? formatCurrency(quotation.summary.totalValue, quotation.summary.currency)
                      : 'N/A'
                    }
                  </td>
                  {/* Total Items Cell */}
                  <td className="text-right tabular-nums">{quotation.summary?.totalItems ?? 'N/A'}</td>
                  {/* Status Cell. D161.3: once the RFQ has a confirmed order the quote is Confirmed with
                      the order, as its header says; its own status choices step back (D104.14). */}
                  <td>
                    <div className="relative flex flex-col items-start gap-0.5">
                      <button
                        onClick={(e) => toggleDropdown(e, quotation._id)}
                        disabled={updatingStatus === quotation._id}
                        aria-haspopup="menu"
                        aria-expanded={openDropdown === quotation._id}
                        className="inline-flex items-center gap-1 rounded-full hover:opacity-80 disabled:opacity-50"
                      >
                        <StatusBadge status={order ? 'Confirmed' : formatStatus(quotation.status)} />
                        <ChevronDown aria-hidden="true" className="h-3.5 w-3.5 text-slate-400" />
                      </button>
                      {order && (
                        <Link
                          to={`/confirmed-orders/${order._id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="whitespace-nowrap text-xs text-primary-500 hover:underline"
                        >
                          {order.quotationNumber ? `Order ${order.quotationNumber}` : 'Open the order'}
                        </Link>
                      )}

                      {openDropdown === quotation._id && menuPos &&
                        createPortal(
                          <div
                            role="menu"
                            style={menuPos}
                            className="fixed z-[9999] min-w-[170px] rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {!order && STATUS_CHOICES.map((choice) => (
                              <button
                                key={choice.value}
                                role="menuitem"
                                onClick={() => handleStatusChange(quotation._id, choice.value)}
                                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50"
                              >
                                <StatusBadge status={choice.label} />
                              </button>
                            ))}
                            {/* Delete — e.g. to clean up a quote left by a deleted RFQ (QA Ticket 1) */}
                            {!order && <div className="my-1 border-t border-slate-100" />}
                            <button
                              role="menuitem"
                              onClick={() => handleDelete(quotation._id)}
                              disabled={updatingStatus === quotation._id}
                              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50 disabled:opacity-50"
                            >
                              <Trash2 aria-hidden="true" className="h-4 w-4" />
                              Delete quotation
                            </button>
                          </div>,
                          document.body
                        )}
                    </div>
                  </td>
                  {/* Action Cell */}
                  <td>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Open quotation"
                      icon={<ArrowRight aria-hidden="true" className="h-4 w-4" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRowClick(quotation._id);
                      }}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination
        page={pagination.page}
        pageSize={pagination.limit}
        total={pagination.total}
        onPageChange={onPageChange}
        noun="quotations"
      />

      <ErrorModal
        isOpen={refusal !== null}
        title={refusal?.title}
        message={refusal?.message || ''}
        onClose={() => setRefusal(null)}
      />
    </div>
  );
};
